// GDPR "download my data" export: gathers everything Firestore holds about the
// logged-in customer (their own profile + orders + issued tickets/passes -
// the same collections Orders.jsx already reads) and bundles it into a zip
// containing CSV files (machine-readable, satisfies GDPR Art. 20's
// "structured, machine-readable" requirement - a PDF would not) plus one
// self-contained HTML file for a human to actually read.
//
// Deliberately does NOT replicate Orders.jsx's fallback scan of the top-level
// "orders" collection (which fetches every order in the whole app and filters
// client-side) - that's a broader, pre-existing scan pattern unrelated to this
// feature, and not something to build further on. This only reads
// subcollections under the user's own doc, which is what Firestore's rules
// actually scope to that user.

import { supabase } from "../../supabase";
import { fetchOwnProfile } from "../../utils/userProfile";
import JSZip from "jszip";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

function toIsoString(value) {
  if (!value) return "";
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  // Tickets store activatedAt/printedAt as raw epoch-ms numbers, not Timestamps/ISO strings.
  if (typeof value === "number") return new Date(value).toISOString();
  // Some fields (e.g. profile.memberSince) are stored as Date.toString()-style
  // strings like "Tue, 20 May 2025 12:10:27 GMT", not ISO - parse rather than
  // assume, otherwise slicing the first 10 chars elsewhere produces garbage.
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  }
  return String(value);
}

export async function gatherUserExportData(user) {
  const profileRow = await fetchOwnProfile(user.id);
  const profile = { email: user.email.toLowerCase(), ...(profileRow || {}) };

  const { data: userRow } = await supabase.from("users").select("id").eq("auth_user_id", user.id).maybeSingle();
  if (!userRow) return { profile, orders: [] };

  const { data: orderRows } = await supabase.from("orders").select("*").eq("user_id", userRow.id);
  const orderIds = (orderRows || []).map((o) => o.id);
  if (orderIds.length === 0) return { profile, orders: [] };

  const [{ data: orderItems }, { data: tickets }, { data: klippekort }] = await Promise.all([
    supabase.from("order_items").select("*").in("order_id", orderIds),
    supabase.from("tickets").select("*").in("order_id", orderIds),
    supabase.from("klippekort").select("*").in("order_id", orderIds),
  ]);

  const orders = orderRows.map((order) => ({
    orderReference: order.order_reference,
    datePurchased: order.date_purchased,
    totalPrice: order.total_price,
    discountCode: order.discount_code,
    discountAmount: order.discount_amount,
    pspReference: order.psp_reference,
    captureStatus: order.capture_status,
    items: (orderItems || [])
      .filter((i) => i.order_id === order.id)
      .map((i) => ({ name: i.name, quantity: i.quantity })),
    ticketItems: [
      ...(tickets || [])
        .filter((t) => t.order_id === order.id)
        .map((t) => ({
          _type: "myTickets", name: t.name, category: t.category, price: t.price,
          quantity: t.quantity, ticketRef: t.ticket_ref || t.id,
          activatedAt: t.activated_at, printedAt: t.printed_at, usedAt: t.used_at,
          isPrinted: (t.printed_from || "").toLowerCase() === "kiosk",
        })),
      ...(klippekort || [])
        .filter((k) => k.order_id === order.id)
        .map((k) => ({
          _type: "myKlippekort", name: k.name, category: "klippekort",
          ticketRef: k.id,
        })),
    ],
  }));

  return { profile, orders };
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const str = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
}

function toCsv(rows, columns) {
  const lines = rows.map((row) => columns.map((col) => csvEscape(row[col])).join(","));
  return [columns.join(","), ...lines].join("\r\n");
}

function buildProfileCsv(profile) {
  const columns = ["email", "firstName", "lastName", "phone", "dob", "memberId", "points", "memberSince", "createdAt"];
  const row = {
    ...profile,
    memberSince: toIsoString(profile.memberSince),
    createdAt: toIsoString(profile.createdAt),
  };
  return toCsv([row], columns);
}

function buildOrdersCsv(orders) {
  const columns = ["orderReference", "datePurchased", "totalPrice", "pointsEarned", "discountCode", "discountAmount", "pspReference", "captureStatus", "items"];
  const rows = orders.map((order) => ({
    ...order,
    datePurchased: toIsoString(order.datePurchased),
    items: order.items || [],
  }));
  return toCsv(rows, columns);
}

function buildTicketsCsv(orders) {
  // Raw stored fields, not a derived/guessed "status" - ticket usage tracking
  // differs by category (round-based tickets like golf use usedAt/isActive,
  // most others use activatedAt/printedAt), so exporting what's actually
  // stored is more accurate than assuming one universal field.
  const columns = ["orderReference", "type", "name", "category", "price", "quantity", "ticketRef", "activatedAt", "printedAt", "usedAt", "isPrinted"];
  const rows = orders.flatMap((order) =>
    (order.ticketItems || []).map((item) => ({
      orderReference: order.orderReference,
      type: item._type,
      name: item.name,
      category: item.category,
      price: item.price,
      quantity: item.quantity,
      ticketRef: item.ticketRef || item.id,
      activatedAt: toIsoString(item.activatedAt),
      printedAt: toIsoString(item.printedAt),
      usedAt: toIsoString(item.usedAt),
      isPrinted: item.isPrinted || false,
    }))
  );
  return toCsv(rows, columns);
}

function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function buildHtmlReport({ profile, orders }) {
  const profileRows = [
    ["Navn", `${profile.firstName || ""} ${profile.lastName || ""}`.trim()],
    ["E-post", profile.email],
    ["Telefon", profile.phone],
    ["Fødselsdato", profile.dob],
    ["Medlems-ID", profile.memberId],
    ["Poeng", Math.round((profile.points ?? 0) * 100) / 100],
    ["Medlem siden", toIsoString(profile.memberSince).slice(0, 10)],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`)
    .join("");

  const orderRows = orders
    .map((order) => {
      const items = (order.items || [])
        .map((item) => `${escapeHtml(item.name)} (x${item.quantity})`)
        .join(", ");
      return `<tr>
        <td>${escapeHtml(order.orderReference)}</td>
        <td>${escapeHtml(toIsoString(order.datePurchased).slice(0, 10))}</td>
        <td>${escapeHtml(order.totalPrice)} kr</td>
        <td>${items}</td>
      </tr>`;
    })
    .join("");

  const ticketRows = orders
    .flatMap((order) => (order.ticketItems || []).map((item) => ({ ...item, orderReference: order.orderReference })))
    .map(
      (item) => {
        const usedTimestamp = item.usedAt || item.activatedAt || item.printedAt;
        const status = usedTimestamp ? toIsoString(usedTimestamp).slice(0, 10) : "Ikke brukt";
        return `<tr>
        <td>${escapeHtml(item.orderReference)}</td>
        <td>${escapeHtml(item.name)}</td>
        <td>${escapeHtml(item._type)}</td>
        <td>${escapeHtml(status)}</td>
      </tr>`;
      }
    )
    .join("");

  return `<!doctype html>
<html lang="no">
<head>
<meta charset="utf-8">
<title>Mine data - Playworld</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; }
  h1 { font-size: 22px; }
  h2 { font-size: 17px; margin-top: 36px; border-bottom: 2px solid #FFC327; padding-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e2e2e2; font-size: 14px; }
  th { color: #555; width: 160px; }
  .meta { color: #777; font-size: 13px; }
  .empty { color: #777; font-style: italic; font-size: 14px; }
</style>
</head>
<body>
  <h1>Mine data hos Playworld</h1>
  <p class="meta">Eksportert ${escapeHtml(new Date().toISOString().slice(0, 10))}</p>

  <h2>Profil</h2>
  <table>${profileRows}</table>

  <h2>Bestillinger</h2>
  ${orders.length ? `<table><tr><th>Referanse</th><th>Dato</th><th>Sum</th><th>Varer</th></tr>${orderRows}</table>` : '<p class="empty">Ingen bestillinger funnet.</p>'}

  <h2>Billetter</h2>
  ${orders.some((o) => o.ticketItems?.length) ? `<table><tr><th>Bestilling</th><th>Navn</th><th>Type</th><th>Status</th></tr>${ticketRows}</table>` : '<p class="empty">Ingen billetter funnet.</p>'}
</body>
</html>`;
}

export { buildProfileCsv, buildOrdersCsv, buildTicketsCsv, buildHtmlReport, toCsv, csvEscape };

export async function downloadUserDataExport(user) {
  const data = await gatherUserExportData(user);

  const zip = new JSZip();
  zip.file("profile.csv", buildProfileCsv(data.profile));
  zip.file("orders.csv", buildOrdersCsv(data.orders));
  zip.file("tickets.csv", buildTicketsCsv(data.orders));
  zip.file("data.html", buildHtmlReport(data));

  const fileName = `mine-data-${new Date().toISOString().slice(0, 10)}.zip`;

  // iPhone/Android app: the web view can't download a blob (nothing happened),
  // so save the zip and open the share sheet (save to Files, mail, AirDrop...).
  if (Capacitor.isNativePlatform()) {
    const { uri } = await Filesystem.writeFile({ path: fileName, data: await zip.generateAsync({ type: "base64" }), directory: Directory.Cache });
    try {
      await Share.share({ title: fileName, files: [uri] });
    } catch (err) {
      if (!/cancel/i.test(err?.message || '')) throw err; // closing the share sheet isn't an error
    }
    return;
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
